import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { 
  Box, Container, Typography, Card, Stack, Avatar, Grid, 
  Button, MenuItem, TextField, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Backdrop, CircularProgress,
  Alert, Snackbar, IconButton, Tooltip, Divider,
  FormControl, InputLabel, Select, SelectChangeEvent, Checkbox,
  Badge, Paper, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { 
  CleaningServices as CleanIcon, 
  AssignmentInd as AssignmentIndIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Build as BuildIcon,
  Block as BlockIcon,
  BusinessCenter as BusinessCenterIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DeleteSweep as DeleteSweepIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Hotel as HotelIcon,
  MeetingRoom as MeetingRoomIcon,
  Bed as BedIcon,
  BookOnline as BookOnlineIcon,
  Warning as WarningIcon,
  Engineering as EngineeringIcon
} from '@mui/icons-material';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types based on actual API responses
interface Room {
  id: number;
  product_id: string;
  name: string;
  item_status?: 'dirty' | 'out_of_order' | 'clean' | 'maintenance' | 'management';
  assigned_to?: string;
  assigned_housekeeper_name?: string;
  qty: number;
  price: string;
  totalQtyPosted?: number;
  is_assigned?: boolean;
  assignment_status?: string | null;
  room_status?: string;
  is_booked_today?: number;
  selected?: boolean;
}

interface Housekeeper {
  user_id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  roleID: number;
}

// Room status options
const ROOM_STATUS_OPTIONS = [
  { value: 'clean', label: 'Clean', color: '#10b981', bgColor: '#e8f5e8', icon: <CheckCircleOutlineIcon /> },
  { value: 'dirty', label: 'Dirty', color: '#f59e0b', bgColor: '#fff4e5', icon: <DeleteSweepIcon /> },
  { value: 'out_of_order', label: 'Out of Order', color: '#ef4444', bgColor: '#ffebee', icon: <BlockIcon /> },
  { value: 'maintenance', label: 'Maintenance', color: '#f97316', bgColor: '#fff4e5', icon: <BuildIcon /> },
  { value: 'management', label: 'Management', color: '#8b5cf6', bgColor: '#f3e5f5', icon: <BusinessCenterIcon /> },
];

// Stat Card Component for better performance
const StatCard = React.memo(({ title, value, color, bgColor, icon }: any) => (
  <Grid item xs={6} sm={3} md={2.4}>
    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: bgColor }}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
        {icon}
        <Typography variant="h5" sx={{ color }}>{value}</Typography>
      </Stack>
      <Typography variant="body2" color="textSecondary">{title}</Typography>
    </Paper>
  </Grid>
));

// Room Card Component for better performance
const RoomCard = React.memo(({ 
  room, 
  selectionMode, 
  isSelected, 
  onSelect, 
  onAssign, 
  onStatusUpdate,
  isAssigned,
  isBooked,
  statusStyle 
}: any) => {
  const extractRoomNumber = (roomName: string): string => {
    const match = roomName.match(/ROOM\s+(\d+)/i);
    return match ? match[1] : roomName;
  };

  const getAssignedHousekeeperName = (room: Room): string => {
    return room.assigned_housekeeper_name || 'Not Assigned';
  };

  return (
    <Card 
      sx={{ 
        position: 'relative',
        border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
        cursor: selectionMode ? 'pointer' : 'default',
        opacity: isSelected ? 0.95 : 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 3,
          transform: selectionMode ? 'scale(1.02)' : 'none'
        }
      }}
      onClick={() => selectionMode && onSelect(room.product_id)}
    >
      {/* Booked/Available Indicator - Top Banner */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '6px',
          bgcolor: isBooked ? '#d32f2f' : '#2e7d32',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4
        }}
      />

      {/* Selection Checkbox - Properly positioned */}
      {selectionMode && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 12, 
            left: 12, 
            zIndex: 10,
            bgcolor: 'white',
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 1
          }}
        >
          <Checkbox
            checked={isSelected}
            size="small"
            icon={<CheckBoxOutlineBlankIcon />}
            checkedIcon={<CheckBoxIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(room.product_id);
            }}
            sx={{
              p: 0,
              '& .MuiSvgIcon-root': {
                fontSize: 24
              }
            }}
          />
        </Box>
      )}

      <Box sx={{ p: 2, pt: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, ml: selectionMode ? 4 : 0 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>{extractRoomNumber(room.name)}</Typography>
            <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 150 }}>
              {room.name}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: statusStyle.bgColor }}>
            <CleanIcon sx={{ color: statusStyle.color }} />
          </Avatar>
        </Stack>

        {/* Booked/Available Status Badge - Non-clickable */}
        <Chip
          icon={isBooked ? <BookOnlineIcon /> : <BedIcon />}
          label={isBooked ? 'Booked' : 'Available'}
          size="small"
          sx={{
            bgcolor: isBooked ? '#ffebee' : '#e8f5e8',
            color: isBooked ? '#d32f2f' : '#2e7d32',
            mb: 1,
            mr: 1,
            '& .MuiChip-icon': { 
              color: isBooked ? '#d32f2f' : '#2e7d32' 
            }
          }}
        />

        {/* Room Status Chip */}
        <Chip
          icon={statusStyle.icon}
          label={room.item_status || room.room_status || 'Available'}
          size="small"
          sx={{
            bgcolor: statusStyle.bgColor,
            color: statusStyle.color,
            mb: 2,
            '& .MuiChip-icon': { color: statusStyle.color }
          }}
        />

        {/* Assignment Info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">Assigned To</Typography>
          <Typography variant="body2" fontWeight={500}>
            {getAssignedHousekeeperName(room)}
          </Typography>
          {isAssigned && room.assignment_status && (
            <Typography variant="caption" color="textSecondary">
              {room.assignment_status}
            </Typography>
          )}
        </Box>

        {/* Action Buttons - Only Edit buttons */}
        {!selectionMode && (
          <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AssignmentIndIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onAssign(room);
              }}
              fullWidth
              sx={{
                borderColor: isAssigned ? '#8b5cf6' : '#1976d2',
                color: isAssigned ? '#8b5cf6' : '#1976d2',
                '&:hover': {
                  borderColor: isAssigned ? '#7c3aed' : '#1565c0',
                  bgcolor: isAssigned ? 'rgba(139, 92, 246, 0.04)' : 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              {isAssigned ? 'Reassign' : 'Assign'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                onStatusUpdate(room);
              }}
              sx={{ 
                minWidth: 40,
                borderColor: statusStyle.color,
                color: statusStyle.color,
                '&:hover': {
                  borderColor: statusStyle.color,
                  bgcolor: statusStyle.bgColor
                }
              }}
            >
              <EditIcon fontSize="small" />
            </Button>
          </Stack>
        )}
      </Box>
    </Card>
  );
});

const HousekeepingDashboard_bulk: React.FC = () => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [filteredRooms, setFilteredRooms] = useState<Room[]>([]);
  const [housekeepers, setHousekeepers] = useState<Housekeeper[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [selectedHK, setSelectedHK] = useState<string>('');
  const [openAssign, setOpenAssign] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  
  // Button loading states
  const [isAssigning, setIsAssigning] = useState<boolean>(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
  const [isBulkAssigning, setIsBulkAssigning] = useState<boolean>(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState<boolean>(false);
  
  // Filter state
  const [bookingFilter, setBookingFilter] = useState<string>('all');
  
  // Multi-select states
  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [openBulkAssign, setOpenBulkAssign] = useState(false);
  const [openBulkStatus, setOpenBulkStatus] = useState(false);
  
  // Snackbar states
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Stats - Complete with all categories
  const stats = useMemo(() => ({
    total: rooms.length,
    booked: rooms.filter(r => r.is_booked_today === 1).length,
    available: rooms.filter(r => r.is_booked_today !== 1).length,
    clean: rooms.filter(r => r.item_status === 'clean').length,
    dirty: rooms.filter(r => r.item_status === 'dirty').length,
    outOfOrder: rooms.filter(r => r.item_status === 'out_of_order' || r.room_status === 'out_of_order').length,
    maintenance: rooms.filter(r => r.item_status === 'maintenance' || r.room_status === 'maintenance').length,
    management: rooms.filter(r => r.item_status === 'management' || r.room_status === 'management').length
  }), [rooms]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const roomRes = await axios.post(`${baseUrl()}Housekeepers/getRoomsByBranch`, formData, config);
      const hkRes = await axios.post(`${baseUrl()}Housekeepers/getHousekeepers`, formData, config);

      if (roomRes.data.status === "true" || roomRes.data.status === true) {
        const mappedRooms = roomRes.data.data.map((room: any) => ({
          ...room,
          status: determineRoomStatus(room),
          room_status: room.room_status || determineRoomStatus(room),
          selected: false
        }));
        setRooms(mappedRooms);
        applyFilter(mappedRooms, bookingFilter);
        setSelectedRooms([]);
      }
      
      if (hkRes.data.status) {
        setHousekeepers(hkRes.data.data);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      showNotification('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = useCallback((roomsData: Room[], filter: string) => {
    if (filter === 'all') {
      setFilteredRooms(roomsData);
    } else if (filter === 'booked') {
      setFilteredRooms(roomsData.filter(room => room.is_booked_today === 1));
    } else if (filter === 'available') {
      setFilteredRooms(roomsData.filter(room => room.is_booked_today !== 1));
    }
  }, []);

  const handleFilterChange = (event: React.MouseEvent<HTMLElement>, newFilter: string) => {
    if (newFilter !== null) {
      setBookingFilter(newFilter);
      applyFilter(rooms, newFilter);
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const extractRoomNumber = (roomName: string): string => {
    const match = roomName.match(/ROOM\s+(\d+)/i);
    return match ? match[1] : roomName;
  };

  const determineRoomStatus = (room: any): string => {
    if (room.room_status) return room.room_status;
    if (room.item_status) return room.item_status;
    if (room.is_assigned || room.assigned_housekeeper_name) return 'assigned';
    if (room.qty === 0) return 'sold';
    return 'available';
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Selection handlers
  const handleRoomSelect = useCallback((productId: string) => {
    setSelectedRooms(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });

    setRooms(prev => prev.map(room => 
      room.product_id === productId 
        ? { ...room, selected: !room.selected }
        : room
    ));
    
    setFilteredRooms(prev => prev.map(room => 
      room.product_id === productId 
        ? { ...room, selected: !room.selected }
        : room
    ));
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedRooms.length === filteredRooms.length) {
      setSelectedRooms([]);
      setRooms(prev => prev.map(room => ({ ...room, selected: false })));
      setFilteredRooms(prev => prev.map(room => ({ ...room, selected: false })));
    } else {
      const allRoomIds = filteredRooms.map(room => room.product_id);
      setSelectedRooms(allRoomIds);
      setRooms(prev => prev.map(room => ({ 
        ...room, 
        selected: allRoomIds.includes(room.product_id) 
      })));
      setFilteredRooms(prev => prev.map(room => ({ 
        ...room, 
        selected: true 
      })));
    }
  }, [filteredRooms, selectedRooms.length]);

  const handleClearSelection = useCallback(() => {
    setSelectedRooms([]);
    setRooms(prev => prev.map(room => ({ ...room, selected: false })));
    setFilteredRooms(prev => prev.map(room => ({ ...room, selected: false })));
  }, []);

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode(prev => !prev);
    if (selectionMode) {
      handleClearSelection();
    }
  }, [selectionMode, handleClearSelection]);

  // Bulk actions with loading states
  const handleBulkAssign = async () => {
    if (selectedRooms.length === 0 || !selectedHK) return;
    
    setIsBulkAssigning(true);
    try {
      const assignPromises = selectedRooms.map(roomId => {
        const formData = new FormData();
        formData.append("requestID", requestID());
        formData.append("roomId", roomId);
        formData.append("housekeeperId", selectedHK);
        
        return axios.post(`${baseUrl()}Housekeepers/create`, formData, config);
      });

      const results = await Promise.all(assignPromises);
      const allSuccessful = results.every(res => res.data.status);
      
      if (allSuccessful) {
        showNotification(`Successfully assigned ${selectedRooms.length} room(s)`, 'success');
        setOpenBulkAssign(false);
        setSelectedHK('');
        handleClearSelection();
        setSelectionMode(false);
        await fetchData();
      } else {
        showNotification('Some assignments failed', 'warning');
      }
    } catch (error) {
      showNotification('Error performing bulk assignment', 'error');
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedRooms.length === 0 || !selectedStatus) return;
    
    setIsBulkUpdating(true);
    try {
      const updatePromises = selectedRooms.map(roomId => {
        const formData = new FormData();
        formData.append("requestID", requestID());
        formData.append("roomId", roomId);
        formData.append("status", selectedStatus);
        
        return axios.post(`${baseUrl()}Housekeepers/updateRoomStatus`, formData, config);
      });

      const results = await Promise.all(updatePromises);
      const allSuccessful = results.every(res => res.data.status);
      
      if (allSuccessful) {
        showNotification(`Successfully updated ${selectedRooms.length} room(s)`, 'success');
        setOpenBulkStatus(false);
        setSelectedStatus('');
        handleClearSelection();
        setSelectionMode(false);
        await fetchData();
      } else {
        showNotification('Some updates failed', 'warning');
      }
    } catch (error) {
      showNotification('Error performing bulk update', 'error');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // Single actions with loading states
  const handleAssignSubmit = async () => {
    if (!selectedRoom || !selectedHK) return;
    
    setIsAssigning(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("roomId", selectedRoom.product_id);
      formData.append("housekeeperId", selectedHK);

      const res = await axios.post(`${baseUrl()}Housekeepers/create`, formData, config);
      
      if (res.data.status) {
        showNotification('Room assigned successfully!', 'success');
        setOpenAssign(false);
        setSelectedHK('');
        await fetchData();
      } else {
        showNotification('Failed to assign room', 'error');
      }
    } catch (error) {
      showNotification('Error assigning room', 'error');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedRoom || !selectedStatus) return;
    
    setIsUpdatingStatus(true);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("roomId", selectedRoom.product_id);
      formData.append("status", selectedStatus);

      const res = await axios.post(`${baseUrl()}Housekeepers/updateRoomStatus`, formData, config);
      
      if (res.data.status) {
        showNotification(`Room status updated to ${selectedStatus}`, 'success');
        setOpenStatusDialog(false);
        setSelectedStatus('');
        await fetchData();
      } else {
        showNotification('Failed to update room status', 'error');
      }
    } catch (error) {
      showNotification('Error updating room status', 'error');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Helper functions
  const getStatusStyle = useCallback((status: string) => {
    const option = ROOM_STATUS_OPTIONS.find(opt => opt.value === status);
    return option || ROOM_STATUS_OPTIONS[0];
  }, []);

  const getHousekeeperDisplayName = (hk: Housekeeper): string => {
    return hk.fullName || `${hk.firstName} ${hk.lastName}`.trim() || hk.email;
  };

  const handleOpenAssign = useCallback((room: Room) => {
    setSelectedRoom(room);
    setOpenAssign(true);
  }, []);

  const handleOpenStatusDialog = useCallback((room: Room) => {
    setSelectedRoom(room);
    setSelectedStatus(room.item_status || room.room_status || 'clean');
    setOpenStatusDialog(true);
  }, []);

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
          
          {/* Header */}
          <Box sx={{ bgcolor: '#1976d2', color: 'white', py: 2, px: 3 }}>
            <Container maxWidth="xl">
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  <HotelIcon />
                  <Box>
                    <Typography variant="h5" fontWeight={600}>Housekeeping Dashboard</Typography>
                    <Typography variant="caption">Manage room status and staff assignments</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2}>
                  <Button 
                    variant="contained" 
                    startIcon={selectionMode ? <DeselectIcon /> : <PlaylistAddCheckIcon />}
                    onClick={toggleSelectionMode}
                    disabled={loading}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                  >
                    {selectionMode ? 'Cancel' : 'Bulk Actions'}
                  </Button>
                  <IconButton sx={{ color: 'white' }} onClick={fetchData} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Container>
          </Box>

          <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
            
            {/* Complete Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <StatCard title="Total Rooms" value={stats.total} color="#1976d2" bgColor="#e3f2fd" icon={<HotelIcon sx={{ color: '#1976d2' }} />} />
              <StatCard title="Booked" value={stats.booked} color="#d32f2f" bgColor="#ffebee" icon={<BookOnlineIcon sx={{ color: '#d32f2f' }} />} />
              <StatCard title="Available" value={stats.available} color="#2e7d32" bgColor="#e8f5e8" icon={<BedIcon sx={{ color: '#2e7d32' }} />} />
              <StatCard title="Clean" value={stats.clean} color="#2e7d32" bgColor="#e8f5e8" icon={<CheckCircleIcon sx={{ color: '#2e7d32' }} />} />
              <StatCard title="Dirty" value={stats.dirty} color="#ed6c02" bgColor="#fff4e5" icon={<DeleteSweepIcon sx={{ color: '#ed6c02' }} />} />
              <StatCard title="Out of Order" value={stats.outOfOrder} color="#d32f2f" bgColor="#ffebee" icon={<BlockIcon sx={{ color: '#d32f2f' }} />} />
              <StatCard title="Maintenance" value={stats.maintenance} color="#f97316" bgColor="#fff4e5" icon={<BuildIcon sx={{ color: '#f97316' }} />} />
              <StatCard title="Management" value={stats.management} color="#8b5cf6" bgColor="#f3e5f5" icon={<BusinessCenterIcon sx={{ color: '#8b5cf6' }} />} />
            </Grid>

            {/* Filter Buttons */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" color="textSecondary">Filter by:</Typography>
                <ToggleButtonGroup
                  value={bookingFilter}
                  exclusive
                  onChange={handleFilterChange}
                  size="small"
                >
                  <ToggleButton value="all">All Rooms ({stats.total})</ToggleButton>
                  <ToggleButton value="booked" sx={{ color: '#d32f2f' }}>
                    <BookOnlineIcon sx={{ mr: 1, fontSize: 18 }} />
                    Booked ({stats.booked})
                  </ToggleButton>
                  <ToggleButton value="available" sx={{ color: '#2e7d32' }}>
                    <BedIcon sx={{ mr: 1, fontSize: 18 }} />
                    Available ({stats.available})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Paper>

            {/* Bulk Actions Bar */}
            {selectionMode && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Badge badgeContent={selectedRooms.length} color="primary">
                      <CheckBoxIcon color="action" />
                    </Badge>
                    <Typography>{selectedRooms.length} selected</Typography>
                    <Button size="small" onClick={handleSelectAll} disabled={loading}>
                      {selectedRooms.length === filteredRooms.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selectedRooms.length > 0 && (
                      <Button size="small" onClick={handleClearSelection} disabled={loading}>Clear</Button>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button 
                      variant="contained" 
                      startIcon={<AssignmentIndIcon />}
                      onClick={() => setOpenBulkAssign(true)}
                      disabled={selectedRooms.length === 0 || loading}
                      size="small"
                    >
                      Assign
                    </Button>
                    <Button 
                      variant="contained" 
                      color="warning"
                      startIcon={<EditIcon />}
                      onClick={() => setOpenBulkStatus(true)}
                      disabled={selectedRooms.length === 0 || loading}
                      size="small"
                    >
                      Update Status
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}

            {/* Rooms Grid */}
            {loading && filteredRooms.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredRooms.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography>No rooms match the selected filter</Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {filteredRooms.map((room) => {
                  const statusStyle = getStatusStyle(room.item_status || room.room_status || 'clean');
                  const isAssigned = !!(room.is_assigned || room.assigned_housekeeper_name);
                  const isBooked = room.is_booked_today === 1;
                  const isSelected = room.selected || false;
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={room.id || room.product_id}>
                      <RoomCard
                        room={room}
                        selectionMode={selectionMode}
                        isSelected={isSelected}
                        onSelect={handleRoomSelect}
                        onAssign={handleOpenAssign}
                        onStatusUpdate={handleOpenStatusDialog}
                        isAssigned={isAssigned}
                        isBooked={isBooked}
                        statusStyle={statusStyle}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Container>

          {/* Assignment Modal */}
          <Dialog open={openAssign} onClose={() => !isAssigning && setOpenAssign(false)} maxWidth="xs" fullWidth>
            <DialogTitle>
              {selectedRoom?.is_assigned ? 'Reassign Housekeeper' : 'Assign Housekeeper'}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Room: <strong>{selectedRoom ? extractRoomNumber(selectedRoom.name) : ''}</strong>
              </Typography>
              <TextField
                select
                fullWidth
                label="Select Housekeeper"
                value={selectedHK}
                onChange={(e) => setSelectedHK(e.target.value)}
                size="small"
                disabled={isAssigning}
              >
                {housekeepers.map((hk) => (
                  <MenuItem key={hk.user_id} value={hk.user_id}>
                    {getHousekeeperDisplayName(hk)}
                  </MenuItem>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenAssign(false)} disabled={isAssigning}>Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleAssignSubmit} 
                disabled={!selectedHK || isAssigning}
              >
                {isAssigning ? <CircularProgress size={24} /> : 'Confirm'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Status Modal */}
          <Dialog open={openStatusDialog} onClose={() => !isUpdatingStatus && setOpenStatusDialog(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Update Room Status</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Room: <strong>{selectedRoom ? extractRoomNumber(selectedRoom.name) : ''}</strong>
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="Status"
                  disabled={isUpdatingStatus}
                >
                  {ROOM_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ color: option.color }}>{option.icon}</Box>
                        <span>{option.label}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenStatusDialog(false)} disabled={isUpdatingStatus}>Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleStatusUpdate} 
                disabled={!selectedStatus || isUpdatingStatus}
              >
                {isUpdatingStatus ? <CircularProgress size={24} /> : 'Update'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Bulk Assign Modal */}
          <Dialog open={openBulkAssign} onClose={() => !isBulkAssigning && setOpenBulkAssign(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Bulk Assign Rooms ({selectedRooms.length})</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selected rooms will be assigned to:
              </Typography>
              <TextField
                select
                fullWidth
                label="Select Housekeeper"
                value={selectedHK}
                onChange={(e) => setSelectedHK(e.target.value)}
                size="small"
                disabled={isBulkAssigning}
              >
                {housekeepers.map((hk) => (
                  <MenuItem key={hk.user_id} value={hk.user_id}>
                    {getHousekeeperDisplayName(hk)}
                  </MenuItem>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenBulkAssign(false)} disabled={isBulkAssigning}>Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleBulkAssign} 
                disabled={!selectedHK || isBulkAssigning}
              >
                {isBulkAssigning ? <CircularProgress size={24} /> : 'Assign All'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Bulk Status Modal */}
          <Dialog open={openBulkStatus} onClose={() => !isBulkUpdating && setOpenBulkStatus(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Bulk Update Status ({selectedRooms.length})</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Update status for all selected rooms to:
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>New Status</InputLabel>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  label="New Status"
                  disabled={isBulkUpdating}
                >
                  {ROOM_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ color: option.color }}>{option.icon}</Box>
                        <span>{option.label}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenBulkStatus(false)} disabled={isBulkUpdating}>Cancel</Button>
              <Button 
                variant="contained" 
                color="warning" 
                onClick={handleBulkStatusUpdate} 
                disabled={!selectedStatus || isBulkUpdating}
              >
                {isBulkUpdating ? <CircularProgress size={24} /> : 'Update All'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* Loading */}
          <Backdrop open={loading} sx={{ color: '#fff', zIndex: 1200 }}>
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default HousekeepingDashboard_bulk;